import React, { useRef, useState, useEffect, useMemo } from "react";

import {
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tooltip,
  useTheme,
  useMediaQuery,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  Casino,
  LocalPrintshop,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";
import { v4 as uuidv4 } from "uuid";

import Table from "../Table";
import { LotteryTypeEnum } from "../../models/lottery-type-enum";
import { TablePdfProps } from "./props";
import Figure from "../../models/figure";

const Tables = (props: TablePdfProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [selectedTables, setSelectedTables] = useState(1);
  const [checked, setChecked] = useState(
    props.lottery?.type! === LotteryTypeEnum.Classic ? false : true
  );
  const [printing, setPrinting] = useState(false);
  const [mix, setMix] = useState("");
  const [distributeEvenly, setDistributeEvenly] = useState(false);
  const [loading, setLoading] = useState(true);

  const componentRef = useRef(null);

  const handleTableSelect = (e: { target: { value: string } }) => {
    setSelectedTables(parseInt(e.target.value));
  };

  const handlePrint = () => {
    setPrinting(true);

    setTimeout(() => {
      window.print();
      setPrinting(false);
    }, 1000);
  };

  const distributedFigures: any = useMemo(() => {
    return distributeEvenly
      ? buildSmartDistributedTables(props.figures ?? [], selectedTables)
      : buildTables();
  }, [distributeEvenly, props.figures, selectedTables]);

  function buildTables(): any {
    setLoading(false);
    return Array.from({ length: selectedTables }, () => props.figures);
  }

  function buildSmartDistributedTables<T>(
    figures: T[],
    tableCount: number,
    perTable: number = 16
  ): T[][] {
    const usedFigures = new Set<T>();
    const result: T[][] = [];

    const shuffle = (arr: T[]): T[] => {
      const copy = [...arr];
      for (let i = copy.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    };

    let carryover: T[] = [];

    for (let i = 0; i < tableCount; i++) {
      let tableFigures: T[] = [];

      // 1. Usar el carryover primero
      if (carryover.length > 0) {
        tableFigures.push(...carryover);
        carryover = [];
      }

      // 2. Si faltan figuras para completar la tabla
      const remaining = perTable - tableFigures.length;

      if (remaining > 0) {
        // Refrescar disponibles si ya no alcanza
        let candidates = figures.filter(
          (f) => !usedFigures.has(f) && !tableFigures.includes(f)
        );

        if (candidates.length < remaining) {
          // Si ya no alcanza, reseteamos todo (permitimos repetir quitando el carryover)
          usedFigures.clear();
          candidates = figures.filter((f) => !tableFigures.includes(f));
        }

        const shuffledCandidates = shuffle(candidates);
        const needed = shuffledCandidates.slice(0, remaining);

        tableFigures.push(...needed);

        // Marcar estas como usadas
        needed.forEach((f) => usedFigures.add(f));
      }

      // 3. Guardar el set final de 16
      result.push(tableFigures.slice(0, perTable));
    }
    // console.log("result", result);
    setLoading(false);
    return result;
  }

  return (
    <>
      <div
        className="no-print"
        style={{ width: "100%", textAlign: "right", marginBottom: 50 }}
      >
        <Tooltip
          title={
            "Distribuir figuras equitativamente entre tablas evitando la repetición"
          }
          arrow
        >
          <FormControlLabel
            control={
              <Switch
                checked={distributeEvenly}
                onChange={() => setDistributeEvenly(!distributeEvenly)}
              />
            }
            label="Distribuir figuras"
          />
        </Tooltip>

        <Tooltip title={printing ? "" : "Revolver figuras"} arrow>
          <Button variant="text" onClick={() => setMix((val) => uuidv4())}>
            <Casino />
          </Button>
        </Tooltip>

        {props.lottery?.type! === LotteryTypeEnum.Classic ? null : (
          <Tooltip
            title={
              checked
                ? "Ocultar nombre de figuras"
                : "Mostrar nombre de figuras"
            }
            arrow
          >
            <Button onClick={() => setChecked((prev) => !prev)}>
              {checked ? <VisibilityOff /> : <Visibility />}
            </Button>
          </Tooltip>
        )}

        <Tooltip title={printing ? "" : "Imprimir"} arrow>
          <Button
            disabled={printing}
            onClick={() => {
              handlePrint();
            }}
          >
            <LocalPrintshop />
          </Button>
        </Tooltip>

        <FormControl sx={{ width: isMobile ? 100 : 120 }} size="small">
          <InputLabel key={1}>Tablas</InputLabel>
          <Select
            value={selectedTables.toString()}
            label="Cantidad"
            onChange={handleTableSelect}
          >
            {Array.from({ length: 20 }, (_, index) => (
              <MenuItem key={index} value={index + 1}>
                {index + 1}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div
        id="div-tables"
        key="div-tables"
        ref={componentRef}
        style={{ textAlign: "center", alignItems: "center" }}
      >
        {!loading &&
          distributedFigures!.map(
            (figsForTable: Figure[] | null | undefined, index: number) => (
              <>
                <Table
                  key={index + mix}
                  index={index}
                  tablesSelected={selectedTables}
                  figures={figsForTable}
                  showName={checked}
                />
              </>
            )
          )}
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={false}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
};

export default Tables;
